import { useAuthMachine } from "auth/state/hook";
import { UserOrganization } from "./types";
import {
  CustomerEnvironment,
  AgentLogsDto,
  UserProfileType,
  InvitedUser,
  User,
  IObject,
  ProvisionableSku,
} from "../saastypes";
import { STALE_TIME_UNLIMITED, STALE_TIME_30_SECONDS } from "./constants";
import { RefetchFunction } from "./types";
import { useAuth } from "../AuthProvider";
import { useFetch } from "./query";
import { isNotNilNorEmpty, isValidUUID } from "./helpers";
import _isArray from "lodash/isArray";
import { CLUSTER_ACTION_TYPE, CLUSTER_STATE } from "constants/common";
import {
  FETCH_AVAILABLE_USER_PROFILES,
  FETCH_CURRENT_USER_PROFILE,
} from "../apiPaths";

export function useUserOrganizations(options?: object): {
  data: UserOrganization[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const { authService } = useAuth();
  const [{ loggedWithOrganization, currentProfile }] =
    useAuthMachine(authService);

  const { data, ...rest } = useFetch<UserOrganization[]>(
    ["/userOrganizations", String(currentProfile)],
    {
      ...options,
      enabled: loggedWithOrganization,
      staleTime: STALE_TIME_UNLIMITED,
    }
  );

  return {
    data: data,
    ...rest,
  };
}

export function useClusterDetail(clusterName: string) {
  const { authService } = useAuth();
  const [{ loggedWithOrganization }] = useAuthMachine(authService);
  const { data, ...rest } = useFetch<CustomerEnvironment>(
    `/getCustomerCluster?clusterName=${clusterName}`,
    {
      staleTime: STALE_TIME_30_SECONDS,
      enabled: loggedWithOrganization,
      errorMessage: "Could not fetch cluster:",
    }
  );

  let clusterWasRemoved = false;
  let isMultiTenant = false;
  if (data != null) {
    clusterWasRemoved = [
      CLUSTER_STATE.DELETED,
      CLUSTER_STATE.DELETING,
    ].includes(data.state);
    isMultiTenant = data.multiTenant;
  }

  return {
    data: data,
    ...rest,
    isMultiTenant,
    clusterWasRemoved,
  } as const;
}

export function useClusterListing(options?: object): {
  data: CustomerEnvironment[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
} {
  const { authService } = useAuth();
  const [{ loggedWithOrganization, currentOrganization }] =
    useAuthMachine(authService);
  const { data, ...rest } = useFetch<CustomerEnvironment[]>(
    ["/getCustomerClusters"].concat(String(currentOrganization?.id)), // We want the cluster listing refreshed on organization change
    {
      ...options,
      enabled: loggedWithOrganization,
      staleTime: STALE_TIME_UNLIMITED,
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useAgentLogs(
  clusterName: string,
  page = 1,
  size = 15,
  command?: string
): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);

  const commandParam = isNotNilNorEmpty(command)
    ? `&command=${command}`
    : `&command=${[
        CLUSTER_ACTION_TYPE.UPGRADE_DEPLOYMENT,
        CLUSTER_ACTION_TYPE.SCALE_DEPLOYMENT,
        CLUSTER_ACTION_TYPE.UPDATE_DEPLOYMENT_MEMORY,
        CLUSTER_ACTION_TYPE.ADD_EKS_ADMIN,
        CLUSTER_ACTION_TYPE.PULL_LOGS,
        CLUSTER_ACTION_TYPE.DELETE_POD,
        CLUSTER_ACTION_TYPE.ROLLOUT_RESTART,
        CLUSTER_ACTION_TYPE.ROLLBACK_DEPLOYMENT,
        CLUSTER_ACTION_TYPE.DOWNLOAD_ALL_POD_LOGS,
        CLUSTER_ACTION_TYPE.DOWNLOAD_THREAD_DUMP,
        CLUSTER_ACTION_TYPE.DOWNLOAD_HEAP_DUMP,
        CLUSTER_ACTION_TYPE.GET_TOP_OUTPUT_FROM_POD,
        // Multi-region actions were absent, so they never appeared in Actions History even once the
        // workflows started recording them (saas-workflows#429). CLOUD-1109.
        CLUSTER_ACTION_TYPE.MAKE_MULTIREGION_PEER,
        CLUSTER_ACTION_TYPE.PROMOTE_TO_PRIMARY,
        CLUSTER_ACTION_TYPE.UNPEER_CLUSTERS,
        CLUSTER_ACTION_TYPE.RENAME_MULTIREGION_ENDPOINT,
        CLUSTER_ACTION_TYPE.REPLACE_CLUSTER_DOMAIN,
      ].join(",")}`;
  const { data, ...rest } = useFetch<AgentLogsDto>(
    `/agent/workflowsResults/${clusterName}?page=${page}&size=${size}${commandParam}`,
    {
      enabled: isLoggedInAndVerified,
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useInvitedUsers(options?: object): {
  data: InvitedUser[];
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { authService } = useAuth();
  const [{ loggedWithOrganization, hasAdminOrRootRole, isOrkesUser }] =
    useAuthMachine(authService);
  const { data, ...rest } = useFetch(`/invitedUsers`, {
    ...options,
    enabled: (loggedWithOrganization && hasAdminOrRootRole) || isOrkesUser,
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    data: data as InvitedUser[],
    ...rest,
  };
}

export function useCurrentUsers(options?: object) {
  const { authService } = useAuth();
  const [{ loggedWithOrganization, hasAdminOrRootRole, isOrkesUser }] =
    useAuthMachine(authService);
  const { data, ...rest } = useFetch<User[]>("/users", {
    ...options,
    enabled: (loggedWithOrganization && hasAdminOrRootRole) || isOrkesUser,
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    data: data,
    ...rest,
  };
}

export function useFetchAvailableUserProfiles(option?: IObject) {
  const { authService } = useAuth();
  const [{ loggedWithOrganization }] = useAuthMachine(authService);
  return useFetch<{ available_user_profiles: UserProfileType[] }>(
    FETCH_AVAILABLE_USER_PROFILES,
    {
      ...option,
      enabled: loggedWithOrganization,
      staleTime: STALE_TIME_30_SECONDS,
    }
  );
}

export function useFetchCurrentUserProfile(option?: IObject) {
  const { authService } = useAuth();
  const [{ loggedWithOrganization }] = useAuthMachine(authService);

  return useFetch<{ current_user_profile: UserProfileType }>(
    FETCH_CURRENT_USER_PROFILE,
    {
      ...option,
      enabled: loggedWithOrganization,
      staleTime: STALE_TIME_30_SECONDS,
    }
  );
}

export function useUserIds(
  ids: Array<string>,
  options: Record<string, any> = {}
): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { authService } = useAuth();
  const [{ loggedWithOrganization }] = useAuthMachine(authService);

  const filteredIds = ids
    .filter((id) => isNotNilNorEmpty(id) && isValidUUID(id))
    .sort(); // Sorting so we at least can have some cache
  const { data, ...rest } = useFetch(
    `/users/getByIds?id=${filteredIds.join("&id=")}`,
    {
      enabled:
        loggedWithOrganization &&
        _isArray(filteredIds) &&
        filteredIds.length > 0,
      ...options,
    }
  );
  return {
    data: data,
    ...rest,
  };
}

/**
 * Which clouds, environments and OCU sizes the active organization's subscription allows right
 * now, so a create form can grey out the rest. Unchecked means no gating; the backend still checks
 * on submit, so a missing answer never blocks.
 */
export function useClusterAllocationCheck() {
  return useFetch<{
    checked: boolean;
    /** Cloud, then environment, to allowed SKU names; anything with none is left out. */
    allowed: Record<string, Record<string, string[]>>;
    message?: string;
  }>("/clusterAllocationCheck", { staleTime: STALE_TIME_30_SECONDS });
}

/**
 * The OCU-based SKU fleet selectable when provisioning an AWS cluster. The backend applies the
 * @orkes.io restriction itself and returns an empty list otherwise; `enabled` only avoids the
 * pointless request. `paramsAvailable` (not `enabled`) is used so useFetch's auth-readiness
 * guards are preserved rather than overridden.
 */
export function useProvisionableSkus(enabled: boolean): {
  data: ProvisionableSku[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const { data, ...rest } = useFetch<ProvisionableSku[]>("/provisionableSkus", {
    paramsAvailable: enabled,
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    // useFetch does not validate the body against the type parameter, so anything the
    // endpoint returns arrives here as-is. Callers map over this, and a non-array body --
    // an error envelope, a 5xx page, a proxy response -- turned ".map is not a function"
    // into a blank Create Environment page rather than a missing SKU picker.
    data: Array.isArray(data) ? data : undefined,
    ...rest,
  };
}
